import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    OutlinedInput,
    Box,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BiSearch } from 'react-icons/bi';

const useStyles = makeStyles((theme) => ({
    formControl: {
        width: '100%',
    },
    inputRoot: {
        padding: '4px 10px',
        fontSize: '13px',
        minHeight: '32px',
        height: '32px',
    },
    paper: {
        maxHeight: '220px',
        overflow: 'auto',
        marginTop: '2px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        zIndex: 1300,
    },
    searchInput: {
        marginBottom: theme.spacing(0.5),
        '& .MuiOutlinedInput-root': {
            height: '32px',
            fontSize: '12px',
        },
        '& .MuiOutlinedInput-input': {
            padding: '6px 10px',
        },
    },
    listItem: {
        padding: '3px 6px',
        minHeight: '32px',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    checkbox: {
        padding: '2px 6px',
    },
    chipContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '3px',
        marginTop: '6px',
        minHeight: '24px',
    },
    chip: {
        height: '24px',
        fontSize: '11px',
    },
    emptyMessage: {
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '12px',
    },
}));

/**
 * Componente multi-select genérico con búsqueda
 * 
 * @param {Object} props - Props del componente
 * @param {Array} props.items - Lista de items disponibles
 * @param {Array} props.selected - IDs seleccionados
 * @param {Function} props.onChange - Callback al cambiar selección
 * @param {boolean} props.disabled - Deshabilitar selector
 * @param {boolean} props.loading - Estado de carga
 * @param {string} props.placeholder - Placeholder del input
 * @param {string} props.searchPlaceholder - Placeholder del filtro
 * @param {string} props.displayField - Campo a mostrar (default: 'label')
 * @param {string} props.idField - Campo ID (default: 'id')
 * @returns {JSX.Element} Multi-select genérico
 */
export default function MultiSelectGeneric({
    items = [],
    selected = [],
    onChange = () => {},
    onClose = null,
    disabled = false,
    loading = false,
    placeholder = 'Seleccionar opciones',
    searchPlaceholder = 'Buscar...',
    displayField = 'label',
    idField = 'id',
}) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    React.useEffect(() => {
        if (onClose) {
            setOpen(false);
        }
    }, [onClose]);

    // Filtrar items por búsqueda
    const filteredItems = useMemo(() => {
        if (!searchTerm) return items;
        const searchLower = searchTerm.toLowerCase();
        return items.filter((item) =>
            (item[displayField] || '').toLowerCase().includes(searchLower) ||
            (item[idField] || '').toString().includes(searchLower)
        );
    }, [items, searchTerm, displayField, idField]);

    // Obtener nombres de items seleccionados
    const selectedItemNames = useMemo(() => {
        return selected
            .map((id) => {
                const item = items.find((i) => i[idField] === id);
                return item ? item[displayField] : null;
            })
            .filter(Boolean);
    }, [selected, items, displayField, idField]);

    const handleToggle = (itemId) => {
        const newSelected = selected.includes(itemId)
            ? selected.filter((id) => id !== itemId)
            : [...selected, itemId];
        onChange(newSelected);
    };

    const handleRemoveChip = (itemId) => {
        const newSelected = selected.filter((id) => id !== itemId);
        onChange(newSelected);
    };

    const handleClearAll = () => {
        onChange([]);
        setSearchTerm('');
    };

    return (
        <Box>
            <FormControl variant="outlined" className={classes.formControl} disabled={disabled}>
                <OutlinedInput
                    className={classes.inputRoot}
                    onClick={() => !disabled && setOpen(!open)}
                    readOnly
                    placeholder={selected.length === 0 ? placeholder : ''}
                    value=""
                    endAdornment={
                        loading ? (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ) : null
                    }
                />
            </FormControl>

            {/* Chips de items seleccionados */}
            {selected.length > 0 && (
                <Box className={classes.chipContainer}>
                    {selectedItemNames.map((name, index) => (
                        <Chip
                            key={selected[index]}
                            label={name}
                            onDelete={() => handleRemoveChip(selected[index])}
                            size="small"
                            className={classes.chip}
                            color="primary"
                            variant="outlined"
                        />
                    ))}
                </Box>
            )}

            {/* Dropdown con opciones */}
            {open && !disabled && (
                <Paper className={classes.paper}>
                    <Box p={1}>
                        {/* Búsqueda */}
                        <TextField
                            fullWidth
                            className={classes.searchInput}
                            placeholder={searchPlaceholder}
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <BiSearch size={16} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Lista de items */}
                        {filteredItems.length > 0 ? (
                            <List dense>
                                {filteredItems.map((item) => (
                                    <ListItem
                                        key={item[idField]}
                                        button
                                        className={classes.listItem}
                                        onClick={() => handleToggle(item[idField])}
                                    >
                                        <ListItemIcon className={classes.checkbox}>
                                            <Checkbox
                                                edge="start"
                                                checked={selected.includes(item[idField])}
                                                tabIndex={-1}
                                                disableRipple
                                                size="small"
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item[displayField]}
                                            primaryTypographyProps={{ style: { fontSize: '13px' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Box className={classes.emptyMessage}>
                                No se encontraron opciones
                            </Box>
                        )}

                        {/* Botones de acción */}
                        {selected.length > 0 && (
                            <Box
                                style={{
                                    padding: '8px',
                                    borderTop: '1px solid #e0e0e0',
                                    textAlign: 'right',
                                }}
                            >
                                <Chip
                                    label="Limpiar selección"
                                    size="small"
                                    onClick={handleClearAll}
                                    variant="outlined"
                                    style={{ cursor: 'pointer' }}
                                />
                            </Box>
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}

MultiSelectGeneric.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
        })
    ),
    selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onChange: PropTypes.func,
    onClose: PropTypes.func,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    placeholder: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    displayField: PropTypes.string,
    idField: PropTypes.string,
};
